import { useEffect, useState } from "react";
import axios from "axios";
import { ExternalLink, Loader2 } from "lucide-react";
import { API } from "./shared";
import { usd } from "@/lib/training";

export const stripeUrl = (r) => `https://dashboard.stripe.com/${(r.stripe_session_id || "").startsWith("cs_test") ? "test/" : ""}${r.payment_intent ? `payments/${r.payment_intent}` : `search?query=${encodeURIComponent(r.stripe_session_id)}`}`;

function Stat({ label, value, testid, sub }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
      <p className="text-[11px] font-bold uppercase tracking-wider text-white/40">{label}</p>
      <p data-testid={testid} className="mt-1 text-2xl font-extrabold text-white">{value}</p>
      {sub && <p className="text-[11px] text-white/40">{sub}</p>}
    </div>
  );
}

export default function PaymentsTab({ ctx }) {
  const { token } = ctx;
  const [s, setS] = useState(null);
  const [po, setPo] = useState(null);
  const h = { headers: { "X-Admin-Token": token } };
  useEffect(() => {
    axios.get(`${API}/admin/payments/summary`, h).then((r) => setS(r.data));
    axios.get(`${API}/admin/payments/payouts`, h).then((r) => setPo(r.data)).catch(() => setPo({ available: false }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s) return <Loader2 className="animate-spin text-white/40" />;
  return (
    <>
      <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">Payments</h1>
      <p className="mt-2 text-sm font-medium text-white/50">{s.note}</p>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Gross revenue (Stripe)" value={usd(s.gross_online)} testid="pay-gross" sub={`${s.paid_count} paid online`} />
        <Stat label="Refunded" value={usd(s.refunded_total)} testid="pay-refunded" sub={`${s.refunded_count} registration(s)`} />
        <Stat label="Pending / abandoned" value={`${s.pending_count} / ${s.abandoned_count}`} testid="pay-pending" sub={`${s.failed_count} failed`} />
        <Stat label="Paid offline" value={usd(s.offline_total)} testid="pay-offline" sub={`${s.offline_count} recorded by admin`} />
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-white/40">Revenue by category</p>
          {s.by_category.length === 0 && <p className="mt-2 text-sm text-white/50">No paid registrations yet.</p>}
          {s.by_category.map((c) => <p key={c.category} className="mt-2 flex justify-between text-sm font-semibold text-white/80"><span>{c.label}</span><span>{usd(c.revenue)}</span></p>)}
        </div>
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-white/40">Stripe payouts</p>
          {!po && <Loader2 size={16} className="mt-2 animate-spin text-white/40" />}
          {po && !po.available && <p data-testid="payouts-unavailable" className="mt-2 text-sm text-white/55">{po.reason || "Payouts unavailable."} <a className="underline" href="https://dashboard.stripe.com/payouts" target="_blank" rel="noreferrer">Open Stripe</a></p>}
          {po?.available && <>
            <p className="mt-2 text-sm font-semibold text-white/80">Available {usd(po.balance.available)} · Pending {usd(po.balance.pending)}</p>
            {po.payouts.map((p) => <p key={p.id} className="mt-1 text-xs text-white/60">{p.arrival_date} · {usd(p.amount)} · {p.status}</p>)}
          </>}
        </div>
      </div>
      <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-white/40">Revenue by offering</p>
        {s.by_offering.map((o) => <p key={o.program_id} className="mt-2 flex justify-between gap-3 text-sm font-semibold text-white/80"><span>{o.title} · {o.age_group}</span><span>{o.count} · {usd(o.revenue)}</span></p>)}
      </div>
      <h2 className="mt-10 text-sm font-extrabold uppercase tracking-wider text-white/50">Recent payments</h2>
      <div className="mt-3 space-y-2">
        {s.recent.map((r) => (
          <div key={r.registration_id} data-testid={`payment-${r.registration_id}`} className="rounded-xl bg-white/5 border border-white/10 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-bold text-white">{r.athlete_name} <span className="text-white/40 font-medium">· {r.parent_email}</span></p>
              <p className="text-xs text-white/50">{(r.paid_at || r.created_at || "").slice(0, 10)} · {r.payment_status}{r.refunded_amount ? ` · refunded ${usd(r.refunded_amount)}` : ""}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-extrabold text-white">{usd(r.amount)}</span>
              {r.stripe_session_id && <a data-testid={`payment-stripe-${r.registration_id}`} href={stripeUrl(r)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#60a5fa]">Stripe <ExternalLink size={12} /></a>}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
